/**
 * Event Logger Singleton - Ring buffer implementation for tracking user interactions
 */

import { EVENT_BUFFER_SIZE } from '../config/constants.js';
import { EVENT_TYPES } from './event-types.js';
import logger from './logger.js';
import type { AppState } from '../types/app-state.js';
import type { EventType, EventPayloads, GenericLogEvent, EventStats } from '../types/events.js';

// Event Logger Singleton
const EventLogger = (function() {
  let events: GenericLogEvent[] = []; // Ring buffer of events
  let sequence = 0; // Monotonically increasing sequence number
  let droppedCount = 0; // Counter for dropped events when buffer is full
  let debugMode = false; // Console logging for events
  let clipStarted = new Set<string>(); // Track which clips have logged a clip_started event
  
  // Reference to appState (will be set by the caller)
  let appState: AppState | null = null;
  
  // Set app state reference
  function setAppState(state: AppState): void {
    appState = state;
  }
  
  // Log an event to the ring buffer
  function logEvent<T extends EventType>(type: T, payload: EventPayloads[T] = {} as EventPayloads[T]): void {
    try {
      sequence++;
      
      // Create a new event with timestamp and sequence number
      const event: GenericLogEvent = {
        ts: new Date().toISOString(),
        seq: sequence,
        type: type
      };
      
      // Add clipId if present in payload
      const payloadWithClipId = payload as Record<string, unknown>;
      if (payloadWithClipId && typeof payloadWithClipId['clipId'] === 'string') {
        event.clipId = payloadWithClipId['clipId'];
      } else if (appState && appState.activeClipId) {
        event.clipId = appState.activeClipId;
      }
      
      // Create a shallow clone of the payload to prevent mutation
      if (payload && Object.keys(payload).length > 0) {
        event.payload = Object.assign({}, payload);
      }
      
      // If buffer is full, remove oldest event
      if (events.length >= EVENT_BUFFER_SIZE) {
        events.shift();
        droppedCount++;
      }
      
      // Add the event to the buffer
      events.push(event);
      
      // Debug log if enabled
      if (debugMode) {
        logger.debug('eventlog', { type, event });
      }
    } catch (e) {
      logger.warn('eventlog: Error logging event', e);
    }
  }
  
  // Get stats about the event buffer
  function getStats(): EventStats {
    return {
      count: events.length,
      dropped: droppedCount,
      capacity: EVENT_BUFFER_SIZE
    };
  }
  
  // Get a copy of all events (not a live reference)
  function dumpEvents(): GenericLogEvent[] {
    return JSON.parse(JSON.stringify(events)) as GenericLogEvent[];
  }
  
  // Clear the event buffer
  function clearEvents(): { message: string } {
    events = [];
    droppedCount = 0;
    sequence = 0;
    clipStarted.clear();
    return { message: 'Event buffer cleared' };
  }
  
  // Toggle debug mode
  function setDebug(enabled: boolean): boolean {
    debugMode = !!enabled;
    logger.info(`eventlog: Debug mode ${debugMode ? 'enabled' : 'disabled'}`);
    return debugMode;
  }
  
  // Track if a clip has already logged a start event
  function hasClipStarted(clipId: string): boolean {
    return clipStarted.has(clipId);
  }
  
  // Mark a clip as started
  function markClipStarted(clipId: string): void {
    clipStarted.add(clipId);
  }
  
  // Public API
  return {
    logEvent,
    getStats,
    dump: dumpEvents,
    clear: clearEvents,
    debug: setDebug,
    hasClipStarted,
    markClipStarted,
    setAppState
  };
})();

/**
 * Enhanced logEvent helper function with type validation
 */
export function logEvent<T extends EventType>(type: T, payload: EventPayloads[T] = {} as EventPayloads[T]): void {
  // Check if type is a valid EVENT_TYPES constant value
  const validEventTypes = Object.values(EVENT_TYPES);
  
  if (!validEventTypes.includes(type)) {
    // Log warning for unknown event types
    logger.warn(`eventlog: Unknown event type: "${type}". Please use a constant from EVENT_TYPES.`);
    logger.warn('eventlog: Valid event types', { types: validEventTypes.join(', ') });
  }
  
  // Delegate to the actual EventLogger
  return EventLogger.logEvent(type, payload);
}

export default EventLogger;

// Expose event logger API to window for developer debugging
declare global {
  interface Window {
    __listenUpEvents?: {
      dump: () => GenericLogEvent[];
      stats: () => EventStats;
      clear: () => { message: string };
      debug: (enabled: boolean) => boolean;
    };
  }
}

if (typeof window !== 'undefined') {
  window.__listenUpEvents = {
    dump: EventLogger.dump,
    stats: EventLogger.getStats,
    clear: EventLogger.clear,
    debug: EventLogger.debug
  };
}
